import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import nookies from 'nookies';
import AvailabilityIndicator from '@/components/common/AvailabilityIndicator';
import CallToActionContainer from '@/components/common/callToAction/CallToActionContainer';
import ProductImage from '@/components/product/ProductImage';
import ProductPrice from '@/components/product/ProductPrice';
import { ATTRIBUTES } from '@/constants/product';
import { SEARCH_SOURCE } from '@/constants/search';
import { ProductModel } from '@/models';
import { useAppDispatch, useAppSelector } from '@/redux';
import { endAssistantConversation } from '@/redux/slices/assistant.slice';
import { addCartProduct } from '@/redux/slices/cart.slice';
import { addToast } from '@/redux/slices/toast.slice';
import { useServices } from '@/services/useServices';
import { CallToActionKind } from '@/types';
import { getAttribute, getProductImage } from '@/utils/productUtils';
import styles from './virtual-assistant-product-card.module.scss';

interface VirtualAssistantProductCardProps {
	product: ProductModel;
	rank?: number;
}

export const VirtualAssistantProductCard = ({ product, rank }: VirtualAssistantProductCardProps) => {
	const { t } = useTranslation();
	const router = useRouter();
	const dispatch = useAppDispatch();
	const { datalayerService, searchAnalyticsService } = useServices();
	const storeConf = useAppSelector(({ root }) => root.storeConf);

	const currentItem = product.items?.[0];
	if (!currentItem) {
		return null;
	}

	const productImage = getProductImage(product.image, false, 'thumb');

	const logSearchClick = () => {
		searchAnalyticsService.logClick({
			productId: product._id,
			rank: rank ?? 0,
			storeId: storeConf.storeId,
			source: SEARCH_SOURCE.ASSISTANT,
			conversationId: nookies.get()['assistantSessionId'] || undefined,
		});
	};

	const handleClick = () => {
		logSearchClick();
		datalayerService.pushChatbotEvent('interact');
		dispatch(endAssistantConversation());
		router.push(product.seoKeyword);
	};

	const handleAddToCart = () => {
		logSearchClick();
		dispatch(
			addCartProduct({
				item: currentItem.partnumber,
				qty: 1,
				options: {
					showConfirm: false,
					set: false,
				},
				error: undefined,
			}),
		);
		datalayerService.pushChatbotEvent('addtocart');
		dispatch(addToast({ msg: t('toast.addedProduct'), err: false }));
	};

	const appellation = getAttribute(product, ATTRIBUTES.APPELLATION);
	const couleur = getAttribute(product, ATTRIBUTES.COLOR);
	const cepageDetail = product.attributes?.cepage_detail;
	const catchPhrase = product.catchPhrase;

	const infos = [appellation?.value, couleur?.value].filter(Boolean).join(' - ');

	const getConditioningQuantity = (partnumber: string): number => {
		const parts = partnumber?.split('/');
		const qty = parts?.[parts.length - 1];
		return qty ? parseInt(qty, 10) : 1;
	};

	const conditioningQty = getConditioningQuantity(currentItem.partnumber);
	const shouldDisplayUnitPrice = conditioningQty > 1;
	const hasPrice = currentItem.listPrice > 0;

	return (
		<div className={styles.container}>
			<ProductImage customClassName={styles.image} url={productImage} alt={product.productName} onClick={handleClick} />
			<div className={styles.content}>
				<div>
					<h3 className={styles.name} onClick={handleClick}>
						{product.productName}
					</h3>
					{infos && <p className={styles.infos}>{infos}</p>}
				</div>
				{cepageDetail?.value && <p className={styles.cepages}>{cepageDetail.value}</p>}
				{catchPhrase && <p className={styles.justification}>{catchPhrase}</p>}
			</div>

			<footer className={styles.footer}>
				<div className={styles.priceSection}>
					{hasPrice && (
						<ProductPrice
							customClass={styles.price}
							currentItem={currentItem}
							product={product}
							displayUnitBloc={shouldDisplayUnitPrice}
							showVat={false}
						/>
					)}
					{currentItem.shortdesc && <p className={styles.conditioning}>{currentItem.shortdesc}</p>}
				</div>

				<AvailabilityIndicator
					t={t}
					product={product}
					currentItem={currentItem}
					storeId={storeConf.storeId}
					locale={storeConf.locale}
					size="small"
					labelClassName={styles.availabilityLabel}
				/>

				<div className={styles.buttons}>
					<CallToActionContainer kind={CallToActionKind.SECONDARY} fullWidth onClick={handleClick} buttonSize="small">
						{t('assistant.product.showProduct')}
					</CallToActionContainer>
					{hasPrice && (
						<CallToActionContainer fullWidth onClick={handleAddToCart} buttonSize="small">
							{t('assistant.product.addToCart')}
						</CallToActionContainer>
					)}
				</div>
			</footer>
		</div>
	);
};
